import { Popover, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { DateFilter } from "@/features/reports/schemas/reports";
import { cn } from "@/shared/lib/utils";

interface DateFilterSectionProps {
  dateFilter: DateFilter;
  showCustomDate: boolean;
  onDateFilterChange: (type: DateFilter["type"]) => void;
  onCustomDateChange: (field: "startDate" | "endDate", value: string) => void;
  setShowData: (show: boolean) => void;
}

/**
 * Componente para la sección de filtros de fecha
 * @param {DateFilterSectionProps} props - Las props del componente
 * @returns {React.JSX.Element} El componente DateFilterSection
 */
export function DateFilterSection({
  setShowData,
  dateFilter,
  showCustomDate,
  onDateFilterChange,
  onCustomDateChange,
}: DateFilterSectionProps): React.JSX.Element {
  return (
    <div className="flex flex-1 flex-col sm:flex-row gap-4 justify-evenly">
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Período
        </label>
        <Select
          value={dateFilter.type}
          onValueChange={(value: DateFilter["type"]) => {
            setShowData(false);
            onDateFilterChange(value);
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Seleccionar período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Hoy</SelectItem>
            <SelectItem value="lastWeek">Semana pasada</SelectItem>
            <SelectItem value="currentMonth">Mes actual</SelectItem>
            <SelectItem value="lastMonth">Mes pasado</SelectItem>
            <SelectItem value="lastThreeMonths">Últimos tres meses</SelectItem>
            <SelectItem value="custom">Personalizado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {showCustomDate && (
        <>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha de inicio
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full pl-3 text-left font-normal",
                    !dateFilter.startDate && "text-muted-foreground"
                  )}
                >
                  {dateFilter.startDate ? (
                    format(dateFilter.startDate, "PPP")
                  ) : (
                    <span>Selecciona una fecha</span>
                  )}
                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 bg-white dark:bg-gray-800 rounded-md"
                align="start"
              >
                <Calendar
                  mode="single"
                  selected={dateFilter.startDate}
                  lang="es"
                  onSelect={(date) => {
                    if (date) {
                      onCustomDateChange("startDate", date.toISOString());
                      setShowData(false);
                    }
                  }}
                  disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                  captionLayout="dropdown"
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha de fin
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full pl-3 text-left font-normal",
                    !dateFilter.endDate && "text-muted-foreground"
                  )}
                >
                  {dateFilter.endDate ? (
                    format(dateFilter.endDate, "PPP")
                  ) : (
                    <span>Selecciona una fecha</span>
                  )}
                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 bg-white dark:bg-gray-800 rounded-md"
                align="start"
              >
                <Calendar
                  mode="single"
                  selected={dateFilter.endDate}
                  onSelect={(date) => {
                    if (date) {
                      onCustomDateChange("endDate", date.toISOString());
                      setShowData(false);
                    }
                  }}
                  disabled={(date) =>
                    date > new Date() ||
                    date < new Date("1900-01-01") ||
                    (dateFilter.startDate ? date < dateFilter.startDate : false)
                  }
                  captionLayout="dropdown"
                />
              </PopoverContent>
            </Popover>
          </div>
        </>
      )}
    </div>
  );
}
